/**
 * Centralized brand styling utilities
 *
 * This file contains reusable style objects and utility functions for consistent
 * brand styling across the application.
 */

import type { CSSProperties } from "react";

/**
 * SmartWiz brand gradient colors
 * Used for highlighting brand names and important UI elements
 */
export const BRAND_GRADIENT =
  "linear-gradient(91deg, #3BABFD 0%, #4F67F6 100%)";

/** Mantine gradient prop and SVG stop colors derived from {@link BRAND_GRADIENT}. */
export const BRAND_GRADIENT_STOPS = {
  from: "#3BABFD",
  to: "#4F67F6",
  deg: 91,
} as const;

/**
 * Subtle surface gradients used by SmartWiz billing and contract screens.
 */
export const BRAND_SOFT_SURFACE_GRADIENT =
  "linear-gradient(180deg, #FFFFFF 0%, #F5F9FD 100%)";

export const BRAND_STAT_CARD_GRADIENT =
  "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,251,255,0.96) 100%)";

export const BRAND_PAYMENT_CARD_GRADIENT =
  "radial-gradient(circle at top right, rgba(59, 171, 253, 0.16), transparent 34%), linear-gradient(135deg, #ffffff 0%, #f7fbff 100%)";

/**
 * "Early Access" callout treatment — a soft champagne-into-white surface with a
 * warm gold accent, deliberately distinct from the blue brand gradient so it
 * reads as exclusive/premium rather than as a primary action or a warning.
 */
export const BRAND_EARLY_ACCESS_SURFACE_GRADIENT =
  "linear-gradient(135deg, #FCF7EA 0%, #FFFDF6 55%, #F7EFDA 100%)";

export const BRAND_EARLY_ACCESS_ACCENT_GRADIENT =
  "linear-gradient(96deg, #B8860B 0%, #E3B23C 48%, #C9962F 100%)";

/**
 * Creates a gradient text style object
 *
 * Apply this to text elements to create a gradient effect on the text itself.
 * The text color becomes transparent and the gradient shows through.
 *
 * @example
 * ```tsx
 * <span style={gradientTextStyle()}>SmartWiz</span>
 * ```
 *
 * @example
 * ```tsx
 * // With custom gradient
 * <span style={gradientTextStyle("linear-gradient(90deg, red, blue)")}>
 *   Custom Text
 * </span>
 * ```
 */
export const gradientTextStyle = (
  gradient: string = BRAND_GRADIENT
): CSSProperties => ({
  backgroundImage: gradient,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  display: "inline-block",
});

/**
 * Creates a gradient background style object
 *
 * Apply this to container elements to create a gradient background.
 *
 * @example
 * ```tsx
 * <div style={gradientBackgroundStyle()}>Content</div>
 * ```
 */
export const gradientBackgroundStyle = (
  gradient: string = BRAND_GRADIENT
): CSSProperties => ({
  backgroundImage: gradient,
});
